import { AppShell as DsAppShell, Button, ContextualMenu, Drawer, Rail, TopBar, TopBarAccount, TopBarButton } from '@orbitalwatch/design';
import type { ContextualMenuItems, TopBarEnv } from '@orbitalwatch/design';
import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Icons } from './icons';
import { PageAsideContext } from './pageAside';
import { ShellSlotsContext } from './shellSlotsContext';
import type { ShellSlots, SubNavRegistration } from './shellSlotsContext';
import { MOBILE_MEDIA_QUERY, useMediaQuery } from './useMediaQuery';

/** One rail section. `disabled` entries are shown but inert (space sections before a space is open). */
export interface RailEntry {
    label: string;
    icon: ReactNode;
    to?: string;
    /** Current section; pass it explicitly (a router match would miss the nested page routes). */
    active?: boolean;
    disabled?: boolean;
    /** Count pill; hidden at 0. */
    badge?: number;
    /** Pinned to the bottom of the rail (Settings). */
    bottom?: boolean;
    /** Accessible name when it says more than the label ("Change requests, 3 proposed"). */
    'aria-label'?: string;
}

/** An entry of the product or account menu. */
export interface ShellMenuItem {
    label: string;
    icon?: ReactNode;
    onClick: () => void;
    separatorBefore?: boolean;
}

export interface AppShellProps {
    /** Wordmark next to the brand mark ("Ark"). */
    appName: string;
    /** Route of the product home (the brand link, "/spaces"). */
    homeTo: string;
    /** Environment pill; the library hides it in prod. */
    env?: TopBarEnv;
    /** Top bar context slot: the current space chip. */
    context?: ReactNode;
    /** Accessible name of the rail ("Ark sections"). */
    railLabel: string;
    rail: RailEntry[];
    /** Grid button in the top bar ("Switch product"). */
    products?: { label: string; header?: string; items: ShellMenuItem[] };
    account?: { name: string; email?: string | null; items: ShellMenuItem[] };
    /** No main padding (the reader, which lays out its own columns). */
    bleed?: boolean;
    children: ReactNode;
}

function initialsOf(name: string): string {
    const words = name.trim().split(/[\s@._-]+/).filter(Boolean);
    return (words.length > 1 ? `${words[0][0]}${words[words.length - 1][0]}` : (words[0] ?? '?').slice(0, 1)).toUpperCase();
}

function toMenuItems(items: ShellMenuItem[]): ContextualMenuItems[] {
    return items.map(item => ({ label: item.label, icon: item.icon, onClick: item.onClick, separatorBefore: item.separatorBefore }));
}

/** Product switcher: library `TopBarButton` (grid) opening a library `ContextualMenu`. */
function ProductMenu(props: NonNullable<AppShellProps['products']>) {
    const [open, setOpen] = useState(false);
    const items = useMemo(() => toMenuItems(props.items), [props.items]);
    return (
        <ContextualMenu items={items} header={props.header} label={props.label} align="end" isOpen={open} onOpenChange={setOpen}>
            <TopBarButton label={props.label} icon={<Icons.grid size={20} />} aria-haspopup="menu" aria-expanded={open} />
        </ContextualMenu>
    );
}

/** Account menu: library `TopBarAccount` opening a library `ContextualMenu` headed by the name and e-mail. */
function AccountMenu(props: NonNullable<AppShellProps['account']>) {
    const [open, setOpen] = useState(false);
    const items = useMemo(() => toMenuItems(props.items), [props.items]);
    const header = (
        <span className="ark-account-header">
            <span className="ark-account-name">{props.name}</span>
            {props.email && props.email !== props.name ? <span className="ark-account-email">{props.email}</span> : null}
        </span>
    );
    return (
        <ContextualMenu items={items} header={header} label={`Account: ${props.name}`} align="end" isOpen={open} onOpenChange={setOpen}>
            <TopBarAccount name={props.name} initials={initialsOf(props.name)} aria-expanded={open} />
        </ContextualMenu>
    );
}

/** Phone bar under the top bar: "Menu" (the rail sections), the current section and the screen's sub-nav toggle. */
function PhoneNav(props: { label: string; rail: RailEntry[]; subNav: SubNavRegistration | null; subNavOpen: boolean; onSubNav: () => void }) {
    const [open, setOpen] = useState(false);
    const current = props.rail.find(entry => entry.active);
    const items = useMemo((): ContextualMenuItems[] => props.rail.map(entry => ({
        label: entry.badge ? `${entry.label} (${entry.badge})` : entry.label,
        icon: entry.icon,
        navigateTo: entry.disabled ? undefined : entry.to,
        isDisabled: entry.disabled,
    })), [props.rail]);
    return (
        <div className="ark-mobile-nav">
            {/* The library Button stops click propagation, so it toggles the (controlled) menu itself. */}
            <ContextualMenu items={items} label={props.label} isOpen={open} onOpenChange={setOpen}>
                <Button
                    type="white"
                    size="sm"
                    text="Menu"
                    icon={<Icons.menu size={18} />}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    onClick={() => setOpen(value => !value)}
                />
            </ContextualMenu>
            {props.subNav ? (
                <>
                    <Button
                        type="secondary"
                        size="sm"
                        text={props.subNav.label}
                        icon={<Icons.toc size={16} />}
                        aria-haspopup="dialog"
                        aria-expanded={props.subNavOpen}
                        onClick={props.onSubNav}
                    />
                    {props.subNav.meta ? <span className="ark-mobile-nav-meta">{props.subNav.meta}</span> : null}
                </>
            ) : current ? (
                <span className="ark-mobile-nav-section" aria-current="page">
                    {current.icon}
                    {current.label}
                </span>
            ) : null}
        </div>
    );
}

/**
 * Ark's frame: library `AppShell` with `TopBar` (brand → home, env pill, the space chip, product switcher, account menu),
 * `Rail` (bottom entries pinned) and, while a screen mounts a `ShellSubNav`, the sub-nav column holding it. Below 768px
 * the rail gives way to `PhoneNav` (in the library's sub-nav slot, which it stacks above the page on phones) and the
 * sub-nav moves into a library `Drawer` opened from it.
 */
export function AppShell(props: AppShellProps) {
    const isPhone = useMediaQuery(MOBILE_MEDIA_QUERY);
    const { rail, railLabel } = props;
    const { pathname } = useLocation();

    const [subNav, setSubNav] = useState<SubNavRegistration | null>(null);
    const [target, setTarget] = useState<HTMLElement | null>(null);
    // The phone drawer is open for the address it was opened on: a page chosen in it navigates, which closes it.
    const [drawerPath, setDrawerPath] = useState<string | null>(null);
    const drawerOpen = isPhone && drawerPath === pathname;
    const setDrawerOpen = useCallback((open: boolean) => setDrawerPath(open ? pathname : null), [pathname]);

    const register = useCallback((entry: SubNavRegistration | null) => {
        setSubNav(current => (current?.label === entry?.label && current?.meta === entry?.meta ? current : entry));
    }, []);
    const close = useCallback(() => setDrawerPath(null), []);
    const slots = useMemo((): ShellSlots => ({
        target,
        placement: isPhone ? 'drawer' : 'column',
        register,
        close,
    }), [target, isPhone, register, close]);

    const toRailItem = (entry: RailEntry) => ({
        id: entry.label,
        label: entry.label,
        icon: entry.icon,
        to: entry.disabled ? undefined : entry.to,
        active: !!entry.active,
        disabled: entry.disabled,
        badge: entry.badge,
        'aria-label': entry['aria-label'],
    });
    const railItems = useMemo(() => rail.filter(entry => !entry.bottom).map(toRailItem), [rail]);
    const railBottom = useMemo(() => rail.filter(entry => entry.bottom).map(toRailItem), [rail]);

    const topBar = (
        <TopBar
            brand={{ name: props.appName, to: props.homeTo }}
            env={props.env}
            context={props.context ? <div className="ark-topbar-context">{props.context}</div> : undefined}
            actions={props.products ? <ProductMenu {...props.products} /> : undefined}
            account={props.account ? <AccountMenu {...props.account} /> : undefined}
        />
    );

    const column = subNav && !isPhone ? <div className="ark-subnav" ref={setTarget} /> : undefined;
    const phoneBar = isPhone ? (
        <PhoneNav label={railLabel} rail={rail} subNav={subNav} subNavOpen={drawerOpen} onSubNav={() => setDrawerOpen(!drawerOpen)} />
    ) : undefined;

    return (
        <ShellSlotsContext.Provider value={slots}>
            <DsAppShell
                className={['ark-shell', isPhone ? 'is-phone' : '', column ? 'ark-has-column' : ''].filter(Boolean).join(' ')}
                topBar={topBar}
                rail={isPhone ? undefined : <Rail aria-label={railLabel} items={railItems} bottom={railBottom.length ? railBottom : undefined} />}
                subNav={isPhone ? phoneBar : column}
                bleed={props.bleed}
                mainId="ark-main"
            >
                <PageAsideContext.Provider value={null}>
                    {props.children}
                </PageAsideContext.Provider>
            </DsAppShell>
            {isPhone && subNav ? (
                <Drawer open={drawerOpen} onClose={close} title={subNav.label} eyebrow={subNav.meta}>
                    <div className="ark-subnav-drawer" ref={setTarget} />
                </Drawer>
            ) : null}
        </ShellSlotsContext.Provider>
    );
}
